import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { generatedUiApi } from '@/services/api';
import type { GeneratedEndpointUiVersion, SaveGeneratedUiInput } from '@/types/uiComposer';

/**
 * List the saved generated UIs for an endpoint (task 124). Returns **parent**
 * UIs with their versions nested — use the `@/lib/generatedUi` helpers to reach
 * a version's `source`/`prompt`.
 */
export const useGeneratedUis = (endpointId: string | undefined) =>
  useQuery({
    queryKey: ['generated-endpoint-uis', endpointId],
    queryFn: () => generatedUiApi.list(endpointId).then((res) => res.data),
    enabled: !!endpointId,
  });

/**
 * Fetch a single saved version by its **version** id (its full source).
 * `GET /generated-endpoint-uis/:id` resolves a version, not a parent UI.
 */
export const useGeneratedUi = (id: string | undefined) =>
  useQuery<GeneratedEndpointUiVersion>({
    queryKey: ['generated-endpoint-ui', id],
    queryFn: () => generatedUiApi.getById(id!).then((res) => res.data.data),
    enabled: !!id,
  });

export const useSaveGeneratedUi = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SaveGeneratedUiInput) =>
      generatedUiApi.save(data).then((res) => res.data.data),
    // The response is `{ generatedUi, version }` — the endpoint id lives on the
    // parent. Reading it off the top level produced an `undefined` key that
    // matched no live query, so nothing refreshed after a save (task 396).
    onSuccess: (saved) => {
      queryClient.invalidateQueries({
        queryKey: ['generated-endpoint-uis', saved.generatedUi.endpointId],
      });
    },
  });
};

/**
 * Publish (or re-publish) one saved version to the AI Service's public page
 * (tasks 847, 850). `id` is the **parent** UI id; omitting `versionId` publishes
 * the current head.
 *
 * Both the UI list and the endpoint are invalidated because one call moves
 * pointers on both records: the parent's `publishedVersionId` and the endpoint's
 * `publishedUiId`, which together with `uiExposed` decide whether the public
 * page serves at all.
 */
export const usePublishGeneratedUi = (endpointId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, versionId }: { id: string; versionId?: string }) =>
      generatedUiApi.publish(id, versionId).then((res) => res.data.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['generated-endpoint-uis', endpointId] });
      void queryClient.invalidateQueries({ queryKey: ['endpoint', endpointId] });
      void queryClient.invalidateQueries({ queryKey: ['endpoints'] });
    },
  });
};

/** Take the public page down. Versions are untouched; `uiExposed` is left alone. */
export const useUnpublishGeneratedUi = (endpointId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => generatedUiApi.unpublish(id).then((res) => res.data.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['generated-endpoint-uis', endpointId] });
      void queryClient.invalidateQueries({ queryKey: ['endpoint', endpointId] });
      void queryClient.invalidateQueries({ queryKey: ['endpoints'] });
    },
  });
};

export const useDeleteGeneratedUi = (endpointId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => generatedUiApi.delete(id).then((res) => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['generated-endpoint-uis', endpointId] });
    },
  });
};
